import React, { useEffect, useMemo } from "react";

import { faBriefcase, faCircleUser } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useForm, useWatch } from "react-hook-form";

import { ErrorBaseInfo } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { FormItem } from "src/base-components/FormItem";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Option, Section, Select } from "src/base-components/Select";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { CaseTemplateDropdown } from "src/cases/common/CaseTemplateDropdown";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import {
  CasePropertiesValue,
  CaseTemplateVersionAttributes,
} from "src/clients/case-management";
import {
  CaseUpdateNodeMetaSelection,
  CaseUpdateNodeMetaSelectionTypeEnum,
} from "src/clients/flow-api";
import {
  CaseUpdateNode,
  CaseUpdateNodeData,
} from "src/constants/NodeDataTypes";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  node: CaseUpdateNode;
  displayedError: ErrorBaseInfo | undefined;
} & NodeEditorBaseProps<CaseUpdateNodeData>;

export const CaseUpdateNodeEditor: React.FC<Props> = ({
  immutable,
  node,
  isReactive,
  onUpdate,
  displayedError,
}) => {
  const formMethods = useForm<CaseUpdateNodeData>({
    mode: "onBlur",
    shouldFocusError: false,
    defaultValues: node.data,
    ...(isReactive && { values: node.data }),
  });
  const isCaseInputLocationHighlighted = useIsFieldHighlighted(
    `${node.id}:case_input_location`,
  );

  useSubmitForm({
    onChange: (data: CaseUpdateNodeData) => {
      onUpdate({ newData: data });
    },
    previousValues: node.data,
    disabled: isReactive,
    watch: formMethods.watch,
  });

  useEffect(() => {
    if (displayedError) {
      if (displayedError.loc_type === "field") {
        formMethods.setError(displayedError.loc as keyof CaseUpdateNodeData, {
          message: displayedError.msg as string,
        });
      }
    }
  }, [displayedError, formMethods]);

  const currentTemplateValue = useWatch({
    control: formMethods.control,
    name: "template",
  });

  const { data: templates, isLoading } = useCaseTemplatesWithVersions();

  const selectedTemplateVersion = (() => {
    if (!currentTemplateValue?.id || !currentTemplateValue?.version_id) {
      return undefined;
    }

    const template = templates?.find((t) => t.id === currentTemplateValue.id);
    const version = template?.versions?.find(
      (v) => v.id === currentTemplateValue.version_id,
    );

    if (template && version) {
      return {
        template,
        version,
      };
    }

    return undefined;
  })();

  if (isLoading) {
    return (
      <div className="space-y-3">
        <SkeletonPlaceholder height="h-24" width="w-full" />
        <SkeletonPlaceholder height="h-32" width="w-full" />
        <SkeletonPlaceholder height="h-28" width="w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <FormItem>
          <FormItem.Label isRequired>
            Select the Case type to update
          </FormItem.Label>
          <Controller
            control={formMethods.control}
            name="template"
            render={({ field }) => (
              <CaseTemplateDropdown
                dataLoc="update-case-template-dropdown"
                disabled={immutable}
                error={!!formMethods.formState.errors.template}
                options={templates || []}
                placeholder="Select Case template"
                selected={
                  field.value?.id && field.value?.version_id
                    ? {
                        templateId: field.value.id,
                        versionId: field.value.version_id,
                      }
                    : undefined
                }
                onSelect={(selection) => {
                  const template = templates?.find(
                    (t) => t.id === selection.templateId,
                  );
                  const version = template?.versions?.find(
                    (v) => v.id === selection.versionId,
                  );

                  if (template && version) {
                    field.onChange({
                      id: template.id,
                      version_id: version.id,
                      etag: parseInt(version.attributes.etag ?? "0", 10),
                    });
                  }
                }}
              />
            )}
          />
        </FormItem>
      </Card>

      <Card>
        <FormItem.Label
          description="Provide the data field that contains the Case object in this Decision Flow. The Case will be updated with all properties, blocks, tasks, and actions."
          isRequired
        >
          Locate the Case
        </FormItem.Label>
        <FormItem>
          <Controller
            control={formMethods.control}
            name="case_input_location"
            render={({ field }) => (
              <AutocompleteCodeInput
                disabled={immutable}
                isSearchHighlighted={isCaseInputLocationHighlighted}
                placeholder="case_object"
                prefix="data."
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FormItem>
      </Card>

      <Card>
        <FormItem.Label
          description="On update, only the selected properties are changed; everything else stays as is."
          isRequired
        >
          Select Case properties to update
        </FormItem.Label>
        <Controller
          control={formMethods.control}
          name="selections"
          render={({ field }) => (
            <CasePropertiesSelector
              disabled={immutable}
              value={selectionsToKeys(field.value)}
              version={selectedTemplateVersion?.version.attributes}
              onChange={(selected) => {
                field.onChange(keysToSelections(selected));
              }}
            />
          )}
        />
      </Card>
    </div>
  );
};

const buildSelectionKey = (
  type: CaseUpdateNodeMetaSelectionTypeEnum,
  key?: string,
  statusKey?: string,
): string => `${type}:${key ?? ""}:${statusKey ?? ""}`;

const selectionToKey = (selection: CaseUpdateNodeMetaSelection): string =>
  buildSelectionKey(selection.type, selection.key, selection.status_key);

const keyToSelection = (key: string): CaseUpdateNodeMetaSelection => {
  const [type, selectionKey, statusKey] = key.split(":");
  return {
    type: type as CaseUpdateNodeMetaSelectionTypeEnum,
    ...(selectionKey && { key: selectionKey }),
    ...(statusKey && { status_key: statusKey }),
  };
};

const selectionsToKeys = (
  selections: CaseUpdateNodeMetaSelection[] | undefined,
): string[] => selections?.map(selectionToKey) ?? [];

const keysToSelections = (keys: string[]): CaseUpdateNodeMetaSelection[] =>
  keys.map(keyToSelection);

type CasePropertiesSelectorProps = {
  version?: CaseTemplateVersionAttributes;
  disabled?: boolean;
  value: string[];
  onChange: (selected: string[]) => void;
};

const CasePropertiesSelector: React.FC<CasePropertiesSelectorProps> = ({
  version,
  disabled,
  value,
  onChange,
}) => {
  const { sections, options } = usePropertiesOptions(version);

  // If the value contains keys that are not in the options
  // keep only the valid keys
  // This happens when new template versions are selected
  useEffect(() => {
    const validKeys = new Set(options.map((opt) => opt.key));
    const filteredValue = value.filter((key) => validKeys.has(key));

    if (filteredValue.length !== value.length) {
      onChange(filteredValue);
    }
  }, [value, options, onChange]);

  return (
    <Select
      dataLoc="case-properties-selector"
      disabled={disabled || !version}
      options={options}
      placeholder={!version ? "Select a template first" : "Select properties"}
      searchPlaceholder="Search a property, block, task or action..."
      sections={sections}
      value={value}
      multiple
      searchable
      sectionDividers
      onChange={onChange}
    />
  );
};

const STATIC_ATTRIBUTES: Option[] = [
  {
    key: buildSelectionKey(CaseUpdateNodeMetaSelectionTypeEnum.ASSIGNEE),
    value: "Assignee",
    icon: faCircleUser,
    sectionKey: "attributes",
  },
  {
    key: buildSelectionKey(CaseUpdateNodeMetaSelectionTypeEnum.QUEUE),
    value: "Queue",
    icon: faBriefcase,
    sectionKey: "attributes",
  },
];

const STATIC_SECTIONS: Section[] = [
  { key: "properties", displayValue: "Properties" },
  { key: "attributes", displayValue: null },
  { key: "blocks", displayValue: "Blocks" },
];

type UsePropertiesOptionsResult = {
  sections: Section[];
  options: Option[];
};

const usePropertiesOptions = (
  version: CaseTemplateVersionAttributes | undefined,
): UsePropertiesOptionsResult => {
  const getPropertyIcon = useGetCasePropertyIcon();

  const sections = useMemo((): Section[] => {
    if (!version) return STATIC_SECTIONS;

    const statusProperty = version.case_properties?.status;
    const statusValues =
      statusProperty?.type === "enum" ? statusProperty.values : undefined;

    const statusSubsections = Object.entries(version.workflow_rules ?? {}).map(
      ([statusKey, rule]) => {
        const statusValue = statusValues?.find((v) => v.value === rule.status);
        const color = statusValue?.color as PillVariants;

        return {
          key: statusKey,
          displayValue: (
            <CaseStatusPill
              status={rule.status}
              subStatus={rule.sub_status ?? undefined}
              variant={color ?? "gray"}
            />
          ),
        };
      },
    );

    const tasksActionsSection: Section = {
      key: "tasks-actions",
      displayValue: "Tasks and Actions",
      subsections: statusSubsections,
    };

    return [...STATIC_SECTIONS, tasksActionsSection];
  }, [version]);

  const options = useMemo((): Option[] => {
    if (!version) return [];

    const properties = Object.entries(version.case_properties ?? {}).map(
      ([key, property]): Option => ({
        key: buildSelectionKey(
          CaseUpdateNodeMetaSelectionTypeEnum.PROPERTIES,
          key,
        ),
        value: property.display_name || key,
        icon: getPropertyIcon(property as CasePropertiesValue),
        sectionKey: "properties",
      }),
    );

    const blocks = Object.entries(version.case_blocks_configs ?? {}).map(
      ([key, block]): Option => ({
        key: buildSelectionKey(
          CaseUpdateNodeMetaSelectionTypeEnum.BLOCK_DATA,
          key,
        ),
        value: block.title,
        sectionKey: "blocks",
      }),
    );

    // Tasks and actions grouped by workflow status
    const statusItems: Option[] = [];

    Object.entries(version.workflow_rules ?? {}).forEach(
      ([statusKey, rule]) => {
        // Add tasks for this status
        rule.workflow_tasks_keys?.forEach((taskRef) => {
          const task = version.case_tasks?.[taskRef.task_key];
          if (task) {
            statusItems.push({
              key: buildSelectionKey(
                CaseUpdateNodeMetaSelectionTypeEnum.TASK_FORMS,
                taskRef.task_key,
                statusKey,
              ),
              value: (
                <div className="flex items-center justify-between gap-2">
                  {task.display_name}
                  <Pill variant="gray">
                    <Pill.Text>Task</Pill.Text>
                  </Pill>
                </div>
              ),
              searchText: `${task.display_name} Task`,
              sectionKey: statusKey,
              displayValue: task.display_name,
            });
          }
        });

        // Add actions for this status
        rule.actions?.forEach((actionRef) => {
          const action = version.case_actions?.[actionRef.action_key];
          if (action) {
            statusItems.push({
              key: buildSelectionKey(
                CaseUpdateNodeMetaSelectionTypeEnum.ACTION_FORMS,
                actionRef.action_key,
                statusKey,
              ),
              value: (
                <div className="flex items-center justify-between gap-2">
                  {action.display_name}
                  <Pill variant="gray">
                    <Pill.Text>Action</Pill.Text>
                  </Pill>
                </div>
              ),
              searchText: `${action.display_name} Action`,
              sectionKey: statusKey,
              displayValue: action.display_name,
            });
          }
        });
      },
    );

    return [...STATIC_ATTRIBUTES, ...properties, ...blocks, ...statusItems];
  }, [version, getPropertyIcon]);

  return { sections, options };
};
